import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { AvailablePaymentMethod, paymentMethodChangerUtils } from '../../components';
import { paymentClient } from '../../client/base';
import {
  getInternalPaymentMethodName,
  getPaymentMethodDataOrDefault,
  PAYMENT_METHOD_DATA,
} from '../../shared/paymentMethodChanger/PaymentMethodConstants';
import {
  getProcessedPaymentMethods,
  PaymentMethodWithAvailability,
} from '../../shared/paymentMethodChanger/paymentMethodAvailabilityHelper';
import { useNavigate } from 'react-router';
import { logError } from '../../utils/logger-utils';
import { goToUrl, QueryParams } from '../../utils';
import {
  InitializePaymentMethodContextModel,
  PaymentMethodChangeStatus,
  PaymentMethodContextType,
} from './PaymentMethodContextTypes';
import { AxiosError, HttpStatusCode } from 'axios';
import { getPaymentStatus } from '../../shared/PaymentContextClient';
import {
  orderPaymentMethods,
  pickPreselection,
} from '../../shared/paymentMethodChanger/orderedMethods';

const TEST_PROVIDER_CODE = 'Test';

const shouldSkipPaymentMethodChangeApi = (pm: AvailablePaymentMethod): boolean =>
  pm.internalData.isRedirect || pm.providerCode === TEST_PROVIDER_CODE;

const PaymentMethodContext = React.createContext<PaymentMethodContextType | null>(null);

const PaymentMethodContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [paymentMethodContextInitialized, setPaymentMethodContextInitialized] =
    useState<boolean>(false);
  const [forcePaymentMethodChangerDisabled, setForcePaymentMethodChangerDisabled] = useState(false);
  const [status, setStatus] = useState(PaymentMethodChangeStatus.Init);
  const [paymentId, setPaymentId] = useState<string | undefined>();
  const [queryParams, setQueryParams] = useState<QueryParams>(
    new QueryParams(new URLSearchParams())
  );
  const [nextChangeAllowed, setNextChangeAllowed] = useState<boolean>(true);
  const [chosenPaymentMethod, setChosenPaymentMethod] = useState<
    AvailablePaymentMethod | undefined
  >();
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<AvailablePaymentMethod[]>(
    []
  );
  const navigate = useNavigate();
  const isInitializingRef = useRef(false);
  const isTriggeringChangeRef = useRef(false);

  useEffect(() => {
    return () => {
      isInitializingRef.current = false;
      isTriggeringChangeRef.current = false;
    };
  }, []);

  const setPaymentMethod = useCallback(
    (paymentMethod: AvailablePaymentMethod) => {
      setChosenPaymentMethod(paymentMethod);
      setStatus(shouldSkipPaymentMethodChangeApi(paymentMethod)
        ? PaymentMethodChangeStatus.Success // no api PM change call for redirect methods or TPP
        : PaymentMethodChangeStatus.Init);
      navigate(paymentMethodChangerUtils.generateUrlForPaymentMethod(queryParams, paymentMethod), {
        replace: true,
      });
    },
    [navigate, queryParams]
  );

  const initializePaymentMethodContext = useCallback(
    async ({
      getStatusResponse,
      location,
      paymentId,
      queryParams,
    }: InitializePaymentMethodContextModel) => {
      // prevent concurrent initialization calls
      if (isInitializingRef.current) {
        return;
      }

      isInitializingRef.current = true;

      try {
        setPaymentId(paymentId);
        setQueryParams(queryParams);
        const activeConfigurations = (await paymentClient.getPaymentActiveConfigurations(paymentId))
          .data;

        const allPMs = activeConfigurations.map((ac) => ({
          name: ac.name,
          shortCode: ac.shortCode,
          methodGroupId: ac.providerPaymentMethodGroupId,
          providerCode: ac.providerCode,
          internalData: getPaymentMethodDataOrDefault(ac.shortCode),
          internalName: getInternalPaymentMethodName(ac.shortCode),
          order: ac.order,
          badgeCode: ac.badgeCode,
        }));

        const processedMethods = await getProcessedPaymentMethods(
          allPMs.map((pm) => pm.shortCode),
          PAYMENT_METHOD_DATA
        );

        const pmsWithAvailability = allPMs.map((pm) => {
          const processed = processedMethods.find(
            (p: PaymentMethodWithAvailability) => p.key.toLowerCase() === pm.shortCode.toLowerCase()
          );
          return {
            ...pm,
            isAvailable: processed?.isAvailable ?? true,
            availabilityMessage: processed?.isAvailable
              ? undefined
              : processed?.info.visibilityRequirements || undefined,
          };
        });

        // Unavailable last, then admin-configured Order; backend order where no Order is set.
        const orderedPMs = orderPaymentMethods(pmsWithAvailability);
        setAvailablePaymentMethods(orderedPMs);

        const paymentMethodFromUrl = paymentMethodChangerUtils.getPaymentMethodByPath(
          location.pathname,
          orderedPMs
        );

        const paymentMethodFromGetStatus = orderedPMs.find(
          (x) =>
            x.providerCode === getStatusResponse.providerCode &&
            x.shortCode === getStatusResponse.providerPaymentMethodGroupCode
        );

        const paymentMethod = paymentMethodFromUrl?.paymentMethod ?? paymentMethodFromGetStatus;

        // If no payment method is present we are on the decision page - get status and url both have no PM embeeded in it
        if (!paymentMethod) {
          const order1 = orderedPMs.filter((m) => m.order === 1);
          if (order1.length > 1) {
            logError(
              new Error(
                `Multiple payment methods with order===1: ${order1.map((m) => m.shortCode).join(', ')}`
              )
            );
          }
          const preselected = pickPreselection(orderedPMs);
          if (preselected) {
            setChosenPaymentMethod(preselected);
            setStatus(
              shouldSkipPaymentMethodChangeApi(preselected)
                ? PaymentMethodChangeStatus.Success
                : PaymentMethodChangeStatus.Init
            );
            navigate(
              paymentMethodChangerUtils.generateUrlForPaymentMethod(queryParams, preselected),
              { replace: true }
            );
            return;
          }
          return;
        }

        // 1. Set active payment method to payment method from url or from get status (in this specific order)
        setChosenPaymentMethod(paymentMethod);
        setStatus(shouldSkipPaymentMethodChangeApi(paymentMethod)
          ? PaymentMethodChangeStatus.Success // no api PM change call for redirect methods or TPP
          : PaymentMethodChangeStatus.Init);

        // 2. Navigate if needed
        if (paymentMethod.methodGroupId !== paymentMethodFromUrl?.paymentMethod.methodGroupId) {
          navigate(
            paymentMethodChangerUtils.generateUrlForPaymentMethod(queryParams, paymentMethod),
            {
              replace: paymentMethodFromUrl === undefined,
            }
          );
        }
      } catch (err) {
        logError(err);
      } finally {
        // Marked done on every outcome, failures included: the effect deps never change
        // again, so there is no retry, and leaving it false keeps the boot overlay in
        // paymentWrappers up for the full failsafe on a page that otherwise works.
        setPaymentMethodContextInitialized(true);
        isInitializingRef.current = false;
      }
    },
    [navigate]
  );

  const triggerPaymentMethodChange = useCallback(async () => {
    // prevent concurrent payment method change calls
    if (isTriggeringChangeRef.current) {
      return;
    }

    if (!chosenPaymentMethod || !nextChangeAllowed) {
      return;
    }

    if (
      status === PaymentMethodChangeStatus.Loading ||
      status === PaymentMethodChangeStatus.Success
    ) {
      return;
    }

    isTriggeringChangeRef.current = true;
    setStatus(PaymentMethodChangeStatus.Loading);

    try {
      const providerPaymentMethodGroupId = Number(chosenPaymentMethod.methodGroupId);
      const choosePaymentMethodResult = (
        await paymentClient.choosePaymentMethod({
          paymentId: paymentId,
          providerPaymentMethodGroupId,
        })
      ).data;

      if (choosePaymentMethodResult.isPaymentFinished) {
        goToUrl(choosePaymentMethodResult.redirectUrl);
        return;
      }

      setNextChangeAllowed(choosePaymentMethodResult.ifNextChangeIsPossible);
      setStatus(PaymentMethodChangeStatus.Success);
    } catch (error) {
      if (!paymentId) {
        logError(error);
        setStatus(PaymentMethodChangeStatus.Failed);
        return;
      }

      const axiosError = error as AxiosError;
      if (axiosError.response?.status === HttpStatusCode.UnprocessableEntity) {
        const paymentStatus = (await getPaymentStatus(paymentId)).data;
        const pmFromStatus = availablePaymentMethods.find(
          (x) =>
            x.providerCode === paymentStatus.providerCode &&
            x.shortCode === paymentStatus.providerPaymentMethodGroupCode
        );

        if (pmFromStatus) {
          setPaymentMethod(pmFromStatus);
        } else {
          logError(error);
          setStatus(PaymentMethodChangeStatus.Failed);
        }
      } else {
        logError(error);
        setStatus(PaymentMethodChangeStatus.Failed);
      }
    } finally {
      isTriggeringChangeRef.current = false;
    }
  }, [
    availablePaymentMethods,
    chosenPaymentMethod,
    nextChangeAllowed,
    paymentId,
    setPaymentMethod,
    status,
  ]);

  const contextValue = useMemo(() => {
    return {
      status,
      nextChangeAllowed,
      chosenPaymentMethod,
      availablePaymentMethods,
      paymentMethodContextInitialized,
      forcePaymentMethodChangerDisabled,
      setPaymentMethod,
      triggerPaymentMethodChange,
      initializePaymentMethodContext,
      setForcePaymentMethodChangerDisabled,
    };
  }, [
    status,
    nextChangeAllowed,
    chosenPaymentMethod,
    availablePaymentMethods,
    paymentMethodContextInitialized,
    forcePaymentMethodChangerDisabled,
    setPaymentMethod,
    triggerPaymentMethodChange,
    initializePaymentMethodContext,
    setForcePaymentMethodChangerDisabled,
  ]);

  return (
    <PaymentMethodContext.Provider value={contextValue}>{children}</PaymentMethodContext.Provider>
  );
};

export { PaymentMethodContext, PaymentMethodContextProvider };
